import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { AT_RISK_REASONS, reportsApi } from "@/api";
import type {
  AtRiskQuery,
  AtRiskReason,
  CohortQuery,
  CourseReportQuery,
  ModerationActionsQuery,
  ReportWindow,
  StudentReportQuery,
  TeacherReportQuery,
} from "@/api";
import { QUERY_KEYS } from "@/constants";

/**
 * Hisobot so'rovlari.
 *
 * Hammasida `keepPreviousData`: oyna yoki saralash o'zgarganda eski jadval
 * xira holda turib qoladi, skeletga sakramaydi — raqamlarni solishtirish
 * aynan shu paytda kerak.
 */
export const useTeacherReports = (query: TeacherReportQuery) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_TEACHER_REPORTS, query],
    queryFn: () => reportsApi.teachers(query),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useTeacherReport = (id: number, window: ReportWindow) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_TEACHER_REPORT, id, window],
    queryFn: () => reportsApi.teacher(id, window),
    placeholderData: keepPreviousData,
    enabled: id > 0,
    select: res => res.data,
  });

export const useStudentReports = (query: StudentReportQuery) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_STUDENT_REPORTS, query],
    queryFn: () => reportsApi.students(query),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useStudentReport = (id: number, window: ReportWindow) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_STUDENT_REPORT, id, window],
    queryFn: () => reportsApi.student(id, window),
    placeholderData: keepPreviousData,
    enabled: id > 0,
    select: res => res.data,
  });

export const useCourseReports = (query: CourseReportQuery) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_REPORTS, query],
    queryFn: () => reportsApi.courses(query),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useAtRisk = (query: AtRiskQuery, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_AT_RISK, query],
    queryFn: () => reportsApi.atRisk(query),
    placeholderData: keepPreviousData,
    enabled,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

/**
 * Har bir xavf sababi bo'yicha yozuvlar soni (plitalar uchun).
 *
 * Yig'ma endpoint YO'Q — har sabab uchun `limit: 1` bilan bitta so'rov va
 * `meta.total`. Bir talaba bir necha sabab bilan kirishi mumkin, shuning
 * uchun sonlar yig'indisi jami yozuvlardan ko'p bo'lishi tabiiy.
 */
export const useAtRiskCounts = () => {
  const queries = useQueries({
    queries: AT_RISK_REASONS.map(reason => ({
      queryKey: [QUERY_KEYS.GET_AT_RISK, "count", reason],
      queryFn: () => reportsApi.atRisk({ page: 1, limit: 1, reason }),
      select: (res: Awaited<ReturnType<typeof reportsApi.atRisk>>) => res.meta.total,
    })),
  });

  const counts = Object.fromEntries(
    AT_RISK_REASONS.map((reason, index) => [reason, queries[index]?.data]),
  ) as Record<AtRiskReason, number | undefined>;

  return {
    counts,
    isLoading: queries.some(query => query.isLoading),
    isError: queries.some(query => query.isError),
  };
};

export const useModeratorReport = (window: ReportWindow, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_MODERATOR_REPORT, window],
    queryFn: () => reportsApi.moderators(window),
    placeholderData: keepPreviousData,
    enabled,
    select: res => res.data,
  });

/**
 * Moderatorning o'z ishi (boshqaruv paneli uchun).
 *
 * Navbat holati platforma bo'yicha keladi — "48 soatdan oshganlar" hammaga
 * bir xil; qarorlar esa faqat so'rovchiniki.
 */
export const useMyModerationReport = (window: ReportWindow = {}, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_MY_MODERATION_REPORT, window],
    queryFn: () => reportsApi.myModeration(window),
    placeholderData: keepPreviousData,
    enabled,
    select: res => res.data,
  });

/** `moderatorId` berilmasa — so'rovchining o'z qarorlari (`/moderators/me/actions`). */
export const useModerationActions = (moderatorId: number | null, query: ModerationActionsQuery) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_MODERATION_ACTIONS, moderatorId ?? "me", query],
    queryFn: () =>
      moderatorId === null
        ? reportsApi.myModerationActions(query)
        : reportsApi.moderatorActions(moderatorId, query),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useRunWeeklyReport = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => reportsApi.runWeekly(),
    onSuccess: res => {
      const result = res.data;
      toast.success(
        t("SETTINGS.WEEKLY_SENT", {
          from: result.week.from,
          to: result.week.to,
          count: result.teachers + result.admins + result.moderators,
        }),
      );
    },
  });
};

/** Kogortalar: yozilgan oyi bo'yicha tugatish va faol qolish. */
export const useCohorts = (query: CohortQuery) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COHORTS, query],
    queryFn: () => reportsApi.cohorts(query),
    placeholderData: keepPreviousData,
    select: res => res.data,
  });

/** Kurs voronkasi — dialog ochilgandagina so'raladi. */
export const useCourseFunnel = (courseId: number | null) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_COURSE_FUNNEL, courseId],
    queryFn: () => reportsApi.courseFunnel(courseId as number),
    enabled: courseId !== null,
    select: res => res.data,
  });

/** Oy bo'yicha o'qituvchilarga to'lovlar. */
export const usePayouts = (period: string | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_PAYOUTS, period ?? null],
    queryFn: () => reportsApi.payouts(period),
    placeholderData: keepPreviousData,
    select: res => res.data,
  });

export const useRecordPayout = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { teacherId: number; period: string; reference?: string }) =>
      reportsApi.recordPayout(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PAYOUTS] });
      toast.success(t("PAYOUTS.RECORDED"));
    },
  });
};

export const useCancelPayout = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => reportsApi.cancelPayout(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PAYOUTS] });
      toast.success(t("PAYOUTS.CANCELLED"));
    },
  });
};
